// src/components/ExternalCertUploadModal.jsx
//
// Subir un certificado EXTERNO (instrumentos especiales): el técnico arma el PDF
// por fuera de la app y lo sube. Queda pendiente y el jefe lo aprueba desde
// "Certificados a aprobar", igual que los generados.

import React, { useEffect, useState } from 'react';
import { uploadExternalCertificate } from '../utils/api-certificates';

export default function ExternalCertUploadModal({ open, onClose, instrument, onUploaded }) {
  const [file, setFile] = useState(null);
  const [number, setNumber] = useState('');
  const [calibDate, setCalibDate] = useState('');
  const [venceDate, setVenceDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) { setFile(null); setNumber(''); setCalibDate(''); setVenceDate(''); setErr(null); setBusy(false); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!file) { setErr('Elegí el archivo PDF del certificado.'); return; }
    if (file.type !== 'application/pdf') { setErr('El certificado tiene que ser un PDF (exportá tu Word a PDF).'); return; }
    if (!number.trim()) { setErr('Ingresá el número de certificado.'); return; }
    try {
      setBusy(true);
      const cert = await uploadExternalCertificate({
        instrument,
        file,
        certificateNumber: number.trim(),
        calibrationDate: calibDate || null,
        validUntil: venceDate || null,
      });
      onUploaded?.(cert);
      onClose?.();
    } catch (e2) {
      console.error('[SubirCertExterno]', e2);
      setErr(e2?.message || 'No se pudo subir el certificado.');
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'w-full rounded-md bg-slate-800/60 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500/50';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#12161a] p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">Subir certificado (especial)</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Para instrumentos especiales cuyo certificado se hizo por fuera de la app.
              {instrument?.lab_id != null && <> Instrumento <b className="text-slate-200">#{instrument.lab_id}</b>{instrument?.tag ? ` · ${instrument.tag}` : ''}.</>}
            </p>
          </div>
          <button onClick={onClose} className="px-2 py-1 text-sm rounded text-slate-300 hover:bg-white/10">Cerrar</button>
        </div>

        <div className="rounded-lg border border-sky-500/25 bg-sky-500/[0.06] px-3 py-2 text-xs text-sky-200">
          Se sube como <b>pendiente</b>: el <b>jefe de laboratorio</b> lo revisa y aprueba desde
          “Certificados a aprobar”. Recién ahí queda disponible para el cliente.
        </div>

        {err && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">{err}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs text-slate-400">Archivo (PDF)</label>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm text-slate-300 file:mr-3 file:rounded-md file:border-0 file:bg-sky-600 file:px-3 file:py-1.5 file:text-white hover:file:bg-sky-500"
            />
            {file && <div className="mt-1 text-[11px] text-slate-400">{file.name} · {(file.size / 1024).toFixed(0)} KB</div>}
          </div>

          <div>
            <label className="text-xs text-slate-400">Número de certificado</label>
            <input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="Ej: 6499123" className={inputCls} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400">Fecha de calibración</label>
              <input type="date" value={calibDate} onChange={(e) => setCalibDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-slate-400">Vence</label>
              <input type="date" value={venceDate} onChange={(e) => setVenceDate(e.target.value)} className={inputCls} />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg border border-white/15 text-sm text-slate-300 hover:bg-white/5">Cancelar</button>
            <button type="submit" disabled={busy} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 disabled:opacity-60">
              {busy ? 'Subiendo…' : 'Subir para aprobación'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
